// Tiny URL-driven router: ~50 LOC. Reads `window.location` on mount and on
// `popstate`, exposes `useRoute()` returning the parsed shape, and
// `navigate(path)` that calls `pushState` and notifies subscribers. We
// avoid a router library because the SPA only has 3 routes and the
// dependency cost (~12 KB gzipped) outweighs the value.

import { useEffect, useState } from "react";

export type Route =
  | { kind: "home" }
  | { kind: "history" }
  | { kind: "job"; jobId: string; tab: string }
  | { kind: "admin_approve"; token: string }
  | { kind: "request_submitted" }
  | { kind: "pending" }
  | { kind: "meeting_new" }
  | { kind: "meetings_list" }
  | { kind: "meeting"; meetingId: string }
  | { kind: "transcript"; meetingId: string }
  | { kind: "frames"; meetingId: string }
  | { kind: "settings"; section: SettingsSection }
  | { kind: "edge"; errorKind: string; meetingId: string | null }
  | { kind: "design" };

export type SettingsSection =
  | "account"
  | "organization"
  | "integrations"
  | "defaults"
  | "billing"
  | "api-keys";
const SETTINGS_SECTIONS = new Set<SettingsSection>([
  "account",
  "organization",
  "integrations",
  "defaults",
  "billing",
  "api-keys",
]);

const TAB_DEFAULT = "overview";
const KNOWN_TABS = new Set([
  "overview",
  "transcript",
  "frames",
  "timeline",
  "spec",
  "flow",
]);

export function parseLocation(loc: Location): Route {
  const path = loc.pathname.replace(/\/+$/, "") || "/";
  const params = new URLSearchParams(loc.search);
  if (path === "/" || path === "") return { kind: "home" };
  if (path === "/jobs") return { kind: "history" };
  if (path === "/design") return { kind: "design" };
  if (path === "/request-submitted") return { kind: "request_submitted" };
  if (path === "/pending") return { kind: "pending" };
  if (path === "/admin/approve") {
    return { kind: "admin_approve", token: params.get("t") ?? "" };
  }
  if (path === "/meetings/new") {
    return { kind: "meeting_new" };
  }
  if (path === "/meetings") {
    return { kind: "meetings_list" };
  }
  const transcriptMatch = /^\/meetings\/([^/]+)\/transcript$/.exec(path);
  if (transcriptMatch) {
    return {
      kind: "transcript",
      meetingId: decodeURIComponent(transcriptMatch[1]),
    };
  }
  const framesMatch = /^\/meetings\/([^/]+)\/frames$/.exec(path);
  if (framesMatch) {
    return {
      kind: "frames",
      meetingId: decodeURIComponent(framesMatch[1]),
    };
  }
  const meet = /^\/meetings\/([^/]+)$/.exec(path);
  if (meet) {
    return { kind: "meeting", meetingId: decodeURIComponent(meet[1]) };
  }
  if (path === "/settings") {
    return { kind: "settings", section: "account" };
  }
  const settingsMatch = /^\/settings\/([^/]+)$/.exec(path);
  if (settingsMatch) {
    const section = settingsMatch[1] as SettingsSection;
    return {
      kind: "settings",
      section: SETTINGS_SECTIONS.has(section) ? section : "account",
    };
  }
  const edgeMatch = /^\/edge\/([^/]+)$/.exec(path);
  if (edgeMatch) {
    return {
      kind: "edge",
      errorKind: decodeURIComponent(edgeMatch[1]),
      meetingId: params.get("meeting"),
    };
  }
  const m = /^\/jobs\/([^/]+)$/.exec(path);
  if (m) {
    const tabRaw = (params.get("tab") || TAB_DEFAULT).toLowerCase();
    const tab = KNOWN_TABS.has(tabRaw) ? tabRaw : TAB_DEFAULT;
    return { kind: "job", jobId: decodeURIComponent(m[1]), tab };
  }
  return { kind: "home" };
}

type Listener = () => void;
const listeners = new Set<Listener>();

export function navigate(path: string, replace = false): void {
  const url = path || "/";
  if (replace) window.history.replaceState({}, "", url);
  else window.history.pushState({}, "", url);
  listeners.forEach((l) => l());
}

export function useRoute(): Route {
  const [route, setRoute] = useState<Route>(() => parseLocation(window.location));
  useEffect(() => {
    const refresh = () => setRoute(parseLocation(window.location));
    window.addEventListener("popstate", refresh);
    listeners.add(refresh);
    return () => {
      window.removeEventListener("popstate", refresh);
      listeners.delete(refresh);
    };
  }, []);
  return route;
}

/// URL builders so callsites don't hand-roll templates.
export const routes = {
  home: () => "/",
  history: () => "/jobs",
  job: (jobId: string, tab?: string) =>
    tab && tab !== TAB_DEFAULT
      ? `/jobs/${encodeURIComponent(jobId)}?tab=${tab}`
      : `/jobs/${encodeURIComponent(jobId)}`,
  requestSubmitted: () => "/request-submitted",
  pending: () => "/pending",
  adminApprove: (token: string) =>
    `/admin/approve?t=${encodeURIComponent(token)}`,
  meetingNew: () => "/meetings/new",
  meetingsList: () => "/meetings",
  meeting: (meetingId: string) => `/meetings/${encodeURIComponent(meetingId)}`,
  transcript: (meetingId: string) =>
    `/meetings/${encodeURIComponent(meetingId)}/transcript`,
  frames: (meetingId: string) =>
    `/meetings/${encodeURIComponent(meetingId)}/frames`,
  settings: (section?: SettingsSection) =>
    `/settings${section ? `/${section}` : ""}`,
  edge: (errorKind: string, meetingId?: string) =>
    meetingId
      ? `/edge/${encodeURIComponent(errorKind)}?meeting=${encodeURIComponent(meetingId)}`
      : `/edge/${encodeURIComponent(errorKind)}`,
  design: () => "/design",
};
